import React from 'react';
import AdminNavbar from "../../Navbars/AdminNavbar.js";
import AdminSidebar from '../../Sidebar/AdminSidebar';
import { ApiGatewayApis } from '../../../apis/ApiGatewayApis';
import { useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Plus, Search, Key, Copy, RotateCw, Trash2,
    ChevronLeft, ChevronRight, X, ArrowLeft,
    Shield, Clock, Eye, EyeOff, CheckCircle, XCircle,
    AlertTriangle,
} from 'lucide-react';

function ApiKeyManagement() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [apiKeys, setApiKeys] = React.useState<any[]>([]);
    const [meta, setMeta] = React.useState<any>(null);
    const [page, setPage] = React.useState<number>(1);
    const [search, setSearch] = React.useState<string>('');
    const [typeFilter, setTypeFilter] = React.useState<string>('');
    const [activeFilter, setActiveFilter] = React.useState<string>('');

    // Modals
    const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
    const [showRevokeModal, setShowRevokeModal] = React.useState<any>(null);
    const [showRotateModal, setShowRotateModal] = React.useState<any>(null);
    const [showNewKeyModal, setShowNewKeyModal] = React.useState<string>(''); // Shows newly generated key
    const [actionLoader, setActionLoader] = React.useState<boolean>(false);

    // Create form
    const [createForm, setCreateForm] = React.useState({
        partner_id: '',
        type: 'partner',
        rate_limit_per_minute: 120,
    });

    React.useEffect(() => { loadApiKeys(); }, [page, typeFilter, activeFilter]);

    const loadApiKeys = () => {
        setLoading(true);
        ApiGatewayApis.getAllApiKeys(page, 20, { search, type: typeFilter, is_active: activeFilter })
            .then((response: any) => {
                setLoading(false);
                if (response?.data) {
                    setApiKeys(response.data.data || []);
                    setMeta(response.data.meta || null);
                }
            })
            .catch((error: any) => {
                setLoading(false);
                toast.error('Failed to load API keys');
            });
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        loadApiKeys();
    };

    const handleCreate = (e: React.FormEvent) => {
        e.preventDefault();
        setActionLoader(true);
        ApiGatewayApis.createApiKey({
            ...createForm,
            partner_id: createForm.partner_id ? parseInt(createForm.partner_id) : undefined,
        })
            .then((response: any) => {
                setActionLoader(false);
                if (response?.data?.success) {
                    toast.success('API key created successfully');
                    // Show the new key to the user
                    if (response.data.data?.key) {
                        setShowNewKeyModal(response.data.data.key);
                    }
                    setShowCreateModal(false);
                    setCreateForm({ partner_id: '', type: 'partner', rate_limit_per_minute: 120 });
                    loadApiKeys();
                } else {
                    toast.warn(response?.data?.message || 'Failed to create API key');
                }
            })
            .catch((error: any) => {
                setActionLoader(false);
                toast.error(error?.response?.data?.message || 'Error creating API key');
            });
    };

    const handleRevoke = (keyId: number) => {
        setActionLoader(true);
        ApiGatewayApis.revokeApiKey(keyId)
            .then((response: any) => {
                setActionLoader(false);
                if (response?.data?.success) {
                    toast.success('API key revoked');
                    setShowRevokeModal(null);
                    loadApiKeys();
                } else {
                    toast.warn(response?.data?.message || 'Failed to revoke');
                }
            })
            .catch((error: any) => {
                setActionLoader(false);
                toast.error(error?.response?.data?.message || 'Error revoking key');
            });
    };

    const handleRotate = (keyId: number) => {
        setActionLoader(true);
        ApiGatewayApis.rotateApiKey(keyId)
            .then((response: any) => {
                setActionLoader(false);
                if (response?.data?.success) {
                    toast.success('API key rotated');
                    if (response.data.data?.new_key) {
                        setShowNewKeyModal(response.data.data.new_key);
                    }
                    setShowRotateModal(null);
                    loadApiKeys();
                } else {
                    toast.warn(response?.data?.message || 'Failed to rotate');
                }
            })
            .catch((error: any) => {
                setActionLoader(false);
                toast.error(error?.response?.data?.message || 'Error rotating key');
            });
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.info('Copied to clipboard');
    };

    return (
        <>
            <AdminSidebar title="API Keys" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="API Key Management" />

                <div className='lg:px-10 pb-6 px-6 pt-5 bg-[#FAFAFA] min-h-screen'>

                    {/* Header */}
                    <div className='flex items-center gap-3 mb-5'>
                        <button onClick={() => navigate('/admin/api-gateway')} className='text-gray-500 hover:text-[#E04343]'>
                            <ArrowLeft size={20} />
                        </button>
                        <h1 className='text-[20px] font-bold text-[#263238]'>API Key Management</h1>
                    </div>

                    {/* Search + Filters + Create */}
                    <div className='bg-white shadow-sm rounded-[10px] p-4 mb-4'>
                        <div className='flex flex-col md:flex-row gap-3 justify-between'>
                            <form onSubmit={handleSearch} className='flex gap-2 flex-1'>
                                <div className='relative flex-1 max-w-[350px]'>
                                    <Search size={16} className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400' />
                                    <input
                                        type='text' value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder='Search by key prefix or partner...'
                                        className='bg-white border border-[#DADADA] text-sm rounded-[5px] pl-9 pr-3 py-2 w-full'
                                    />
                                </div>
                                <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] px-3 py-2'>
                                    <option value=''>All Types</option>
                                    <option value='partner'>Partner</option>
                                    <option value='customer'>Customer</option>
                                </select>
                                <select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] px-3 py-2'>
                                    <option value=''>All Status</option>
                                    <option value='1'>Active</option>
                                    <option value='0'>Inactive</option>
                                </select>
                                <button type='submit' className='bg-[#E04343] text-white text-sm px-4 py-2 rounded-[5px] hover:bg-[#c73838]'>
                                    Search
                                </button>
                            </form>
                            <button onClick={() => setShowCreateModal(true)}
                                className='bg-[#E04343] text-white text-sm px-4 py-2 rounded-[5px] hover:bg-[#c73838] flex items-center gap-2'>
                                <Plus size={16} /> New API Key
                            </button>
                        </div>
                    </div>

                    {/* API Keys Table */}
                    <div className='bg-white shadow-sm rounded-[10px] overflow-hidden'>
                        {loading ? (
                            <div className='flex justify-center py-16'>
                                <Oval visible={true} height="35" width="35" color="#E04343" secondaryColor="#FCBF94" />
                            </div>
                        ) : apiKeys.length === 0 ? (
                            <div className='text-center py-16'>
                                <Key size={40} className='mx-auto text-gray-300 mb-3' />
                                <p className='text-[14px] text-[#808080]'>No API keys found</p>
                            </div>
                        ) : (
                            <div className='overflow-x-auto'>
                                <table className='w-full text-left'>
                                    <thead className='bg-gray-50 border-b'>
                                        <tr>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Key Prefix</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Type</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Owner</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Status</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Requests</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Rate Limit</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Last Used</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3 text-right'>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {apiKeys.map((key: any) => (
                                            <tr key={key.id} className='border-b hover:bg-gray-50 transition'>
                                                <td className='px-4 py-3'>
                                                    <span className='text-[13px] font-mono font-medium text-[#263238]'>{key.key_prefix}...</span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${key.type === 'partner' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                                        }`}>
                                                        {key.type}
                                                    </span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className='text-[13px] text-[#808080]'>{key.partner_name || key.user_email || '-'}</span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${key.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {key.is_active ? 'Active' : 'Revoked'}
                                                    </span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className='text-[13px]'>{(key.total_requests || 0).toLocaleString()}</span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className='text-[12px] text-[#808080]'>{key.rate_limit_per_minute || '-'}/min</span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className='text-[12px] text-[#808080]'>
                                                        {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                                                    </span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <div className='flex justify-end gap-1'>
                                                        {key.is_active && (
                                                            <>
                                                                <button onClick={() => setShowRotateModal(key)}
                                                                    className='p-1.5 rounded hover:bg-blue-50' title='Rotate'>
                                                                    <RotateCw size={15} className='text-blue-600' />
                                                                </button>
                                                                <button onClick={() => setShowRevokeModal(key)}
                                                                    className='p-1.5 rounded hover:bg-red-50' title='Revoke'>
                                                                    <Trash2 size={15} className='text-red-500' />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {meta && meta.last_page > 1 && (
                            <div className='flex justify-between items-center px-4 py-3 border-t'>
                                <span className='text-[12px] text-[#808080]'>Showing {meta.from}-{meta.to} of {meta.total}</span>
                                <div className='flex gap-1'>
                                    <button disabled={page <= 1} onClick={() => setPage(page - 1)}
                                        className='p-1.5 rounded hover:bg-gray-100 disabled:opacity-30'>
                                        <ChevronLeft size={16} />
                                    </button>
                                    <span className='px-3 py-1 text-[12px] font-medium'>{page} / {meta.last_page}</span>
                                    <button disabled={page >= meta.last_page} onClick={() => setPage(page + 1)}
                                        className='p-1.5 rounded hover:bg-gray-100 disabled:opacity-30'>
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Create API Key Modal */}
            {showCreateModal && (
                <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[480px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b'>
                            <h3 className='text-[16px] font-semibold'>Create New API Key</h3>
                            <button onClick={() => setShowCreateModal(false)}><X size={18} /></button>
                        </div>
                        <form onSubmit={handleCreate} className='p-5 space-y-4'>
                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>Key Type <span className='text-red-400'>*</span></label>
                                <select value={createForm.type}
                                    onChange={(e) => setCreateForm({ ...createForm, type: e.target.value })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5'>
                                    <option value='partner'>Partner</option>
                                    <option value='customer'>Customer</option>
                                </select>
                            </div>
                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>
                                    {createForm.type === 'partner' ? 'Partner ID' : 'User ID'} <span className='text-red-400'>*</span>
                                </label>
                                <input type='number' required value={createForm.partner_id}
                                    onChange={(e) => setCreateForm({ ...createForm, partner_id: e.target.value })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5'
                                    placeholder={createForm.type === 'partner' ? 'Enter partner ID' : 'Enter user ID'} />
                            </div>
                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>Rate Limit (per minute)</label>
                                <input type='number' min={10} max={1000} value={createForm.rate_limit_per_minute}
                                    onChange={(e) => setCreateForm({ ...createForm, rate_limit_per_minute: parseInt(e.target.value) || 120 })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5' />
                            </div>
                            <button type='submit' disabled={actionLoader}
                                className='bg-[#E04343] text-white py-2.5 rounded-[5px] w-full text-sm font-medium hover:bg-[#c73838] disabled:opacity-50 flex justify-center gap-2'>
                                Create API Key
                                <Oval visible={actionLoader} height="18" width="18" color="#fff" secondaryColor="#FCBF94" />
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Revoke Confirm Modal */}
            {showRevokeModal && (
                <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[400px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b'>
                            <h3 className='text-[16px] font-semibold text-red-600'>Revoke API Key</h3>
                            <button onClick={() => setShowRevokeModal(null)}><X size={18} /></button>
                        </div>
                        <div className='p-5'>
                            <div className='flex items-start gap-3 mb-4'>
                                <AlertTriangle size={20} className='text-red-500 mt-0.5 shrink-0' />
                                <div>
                                    <p className='text-[13px] text-[#808080]'>
                                        This will permanently disable the API key <strong className='font-mono'>{showRevokeModal.key_prefix}...</strong>.
                                        All requests using this key will be rejected immediately.
                                    </p>
                                </div>
                            </div>
                            <div className='flex gap-3'>
                                <button onClick={() => setShowRevokeModal(null)}
                                    className='flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-[5px] text-sm font-medium hover:bg-gray-200'>
                                    Cancel
                                </button>
                                <button onClick={() => handleRevoke(showRevokeModal.id)} disabled={actionLoader}
                                    className='flex-1 bg-red-600 text-white py-2.5 rounded-[5px] text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex justify-center gap-2'>
                                    Revoke
                                    <Oval visible={actionLoader} height="18" width="18" color="#fff" secondaryColor="#FCBF94" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Rotate Confirm Modal */}
            {showRotateModal && (
                <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[400px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b'>
                            <h3 className='text-[16px] font-semibold'>Rotate API Key</h3>
                            <button onClick={() => setShowRotateModal(null)}><X size={18} /></button>
                        </div>
                        <div className='p-5'>
                            <p className='text-[13px] text-[#808080] mb-4'>
                                This will generate a new key and disable <strong className='font-mono'>{showRotateModal.key_prefix}...</strong>.
                                The old key will have a 24-hour grace period before full deactivation.
                            </p>
                            <div className='flex gap-3'>
                                <button onClick={() => setShowRotateModal(null)}
                                    className='flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-[5px] text-sm font-medium hover:bg-gray-200'>
                                    Cancel
                                </button>
                                <button onClick={() => handleRotate(showRotateModal.id)} disabled={actionLoader}
                                    className='flex-1 bg-blue-600 text-white py-2.5 rounded-[5px] text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex justify-center gap-2'>
                                    Rotate
                                    <Oval visible={actionLoader} height="18" width="18" color="#fff" secondaryColor="#FCBF94" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* New Key Display Modal */}
            {showNewKeyModal && (
                <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[520px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b'>
                            <h3 className='text-[16px] font-semibold text-green-700'>API Key Generated</h3>
                            <button onClick={() => setShowNewKeyModal('')}><X size={18} /></button>
                        </div>
                        <div className='p-5'>
                            <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4'>
                                <p className='text-[12px] text-yellow-800 flex items-center gap-2'>
                                    <AlertTriangle size={14} />
                                    Copy this key now. It will not be shown again.
                                </p>
                            </div>
                            <div className='bg-gray-50 border rounded-lg p-3 flex items-center justify-between gap-2'>
                                <code className='text-[13px] font-mono break-all flex-1'>{showNewKeyModal}</code>
                                <button onClick={() => copyToClipboard(showNewKeyModal)}
                                    className='shrink-0 bg-[#E04343] text-white px-3 py-1.5 rounded text-[12px] flex items-center gap-1 hover:bg-[#c73838]'>
                                    <Copy size={14} /> Copy
                                </button>
                            </div>
                            <button onClick={() => setShowNewKeyModal('')}
                                className='mt-4 w-full bg-gray-100 text-gray-700 py-2.5 rounded-[5px] text-sm font-medium hover:bg-gray-200'>
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </>
    );
}

export default ApiKeyManagement;